import {Component, OnInit} from '@angular/core';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {Store} from '@ngrx/store';
import {AppState} from 'app/core/reducers';
import {Router} from '@angular/router';
import {CMS_INDICATIONS, CMS_SPECIFIC_INDICATION, WELCOME} from 'app/core/services/routes.service';
import {AuthRestService} from 'app/core/modules/rest/authentication/auth-rest.service';
import {IndicationBasicModel} from 'app/core/modules/rest/indication/response/indication-basic.model';
import {Observable} from 'rxjs';
import {IndicationActions, IndicationReducer} from '../../../../indication/store';
import {getLanguageEnumByLanguageCode, LanguageEnum} from 'app/core/enums/language.enum';
import {NewIndicationPopupComponent} from '../pop-ups/new-indication-popup/new-indication-popup.component';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DropdownModel} from '@shared/components/dropdown-button/model/dropdown.model';
import {ButtonActionsEnum} from '@shared/components/dropdown-button/enum/button-actions.enum';
import {TranslateService} from '@ngx-translate/core';
import * as fromActions from '../../../../indication/store/indication-actions';
import {IndicationBasicStatusCodeModel} from '@core/modules/rest/indication/response/indication-basic-status-code.model';
import {IndicationRestService} from '@core/modules/rest/indication/indication-rest.service';
import {filter, take} from 'rxjs/operators';
import {IndicationModel} from '@core/modules/rest/indication/response/indication.model';

@UntilDestroy()
@Component({
    selector: 'app-cms-indication-list',
    templateUrl: './cms-indication-list.component.html',
    styleUrls: ['./cms-indication-list.component.scss']
})
export class CmsIndicationListComponent implements OnInit {

    indications$: Observable<IndicationBasicModel[]> = this.store.select(IndicationReducer.indicationsSelector);
    indications: IndicationBasicModel[] = [];
    language: LanguageEnum | undefined;
    showCloneOption = true;
    menuItems: DropdownModel[] = [];
    triggerData: [{ triggerBtnIcon: string; triggerBtnClasses: string; triggerBtnIconSize: string; triggerBtnAriaLabel: any; triggerBtnIconColor: string }];

    constructor(private dialog: MatDialog, private store: Store<AppState>, private router: Router, private authService: AuthRestService, private translateService: TranslateService,
                private indicationService: IndicationRestService) {
    }

    ngOnInit(): void {
        this.language = getLanguageEnumByLanguageCode(localStorage.getItem('languageCode')!);
        this.store.dispatch(IndicationActions.GetIndications({language: this.language}));
        this.indications$.subscribe((response: IndicationBasicModel[]) => {
            this.indications = response;
        });

        this.triggerData = [{
            triggerBtnClasses: 'icon',
            triggerBtnIcon: 'more_horiz',
            triggerBtnIconColor: 'gray-07',
            triggerBtnIconSize: '52',
            triggerBtnAriaLabel: this.translateService.instant('SCREEN_READER.ACTIVATE_DEACTIVATE_USER')
        }];
        this.menuItems = [
            // {
            //     key: ButtonActionsEnum.Delete,
            //     btnCssClasses: 'small outlined',
            //     btnIcon: 'delete',
            //     btnIconSize: '20',
            //     btnIconColor: '',
            //     btnLabelColor: '',
            //     btnLabel: this.translateService.instant('GENERAL.DELETE'),
            //     btnAriaLabel: this.translateService.instant('GENERAL.DELETE') + this.translateService.instant('GENERAL.BUTTON')
            // },
            {
                key: ButtonActionsEnum.Duplicate,
                btnCssClasses: 'small outlined',
                btnIcon: 'copy',
                btnIconSize: '20',
                btnIconColor: '',
                btnLabelColor: '',
                btnLabel: this.translateService.instant('GENERAL.DUPLICATE'),
                btnAriaLabel: this.translateService.instant('GENERAL.DUPLICATE') + this.translateService.instant('GENERAL.BUTTON'),
                disabled: false
            },
            {
                key: ButtonActionsEnum.Edit,
                btnCssClasses: 'small outlined dropdown-item',
                btnIcon: 'edit',
                btnIconSize: '20',
                btnIconColor: '',
                btnLabelColor: '',
                btnLabel: this.translateService.instant('GENERAL.EDIT'),
                btnAriaLabel: this.translateService.instant('GENERAL.EDIT'),
                disabled: false
            }
        ];
    }

    onKeyUp(event: any, id: string): void {
        if (event.key === 'Enter' || event.code === 'Space') {
            this.router.navigate([CMS_SPECIFIC_INDICATION(id)]);
        }
    }

    onLogOut(): void {
        this.authService.logout();
    }

    backButtonAction(): void {
        this.router.navigate([WELCOME]);
    }

    buttonActions(event, indicationId: string): void {
        if (event === ButtonActionsEnum.Duplicate) {
            this.onCloneIndication(indicationId);
        } else if (event === ButtonActionsEnum.Edit) {
            this.onEditIndication(indicationId);
        }
    }

    onCloneIndication(indicationId: string): void {
        this.showCloneOption = false;
        const language = this.language;
        this.store.dispatch(fromActions.CloneIndication({
            indicationId, language, executed: () => {
                this.showCloneOption = true;
            }
        }));
    }

    onOpenIndication(id: string): void {
        this.router.navigate([CMS_INDICATIONS, id]);
    }

    onAddNewIndication(): void {
        this.openIndicationPopUp(null);
    }

    onEditIndication(indicationId: string): void {
        this.indicationService.getIndicationDetails(indicationId).pipe(filter(value => !!value), take(1)).subscribe(data => {
            this.openIndicationPopUp(data);
        });
    }

    openIndicationPopUp(indication: IndicationModel): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.disableClose = true;
        dialogConfig.data = indication;
        const dialogRef = this.dialog.open(NewIndicationPopupComponent, dialogConfig);
        dialogRef.componentInstance.createIndicationEvent.pipe(untilDestroyed(this)).subscribe(data => {
            this.store.dispatch(fromActions.SaveIndication({
                request: data,
                language: this.language,
                executed: (response: IndicationBasicStatusCodeModel) => {
                    if (response.created) {
                        dialogRef.close();
                    } else {
                        if (response.titleMandatoryAlreadyExist) {
                            dialogRef.componentInstance.titleMandatoryExist = true;
                        }
                        if (response.titleEnglishAlreadyExist) {
                            dialogRef.componentInstance.titleEnglishExist = true;
                        }
                        if (response.titleGermanAlreadyExist) {
                            dialogRef.componentInstance.titleGermanExist = true;
                        }
                        if (response.titleSerbianAlreadyExist) {
                            dialogRef.componentInstance.titleSerbianExist = true;
                        }
                        if (response.titleMandatoryMissing) {
                            dialogRef.componentInstance.titleMandatoryMissing = true;
                        }
                        if (response.titleEnglishMissing) {
                            dialogRef.componentInstance.titleEnglishMissing = true;
                        }
                        if (response.titleGermanMissing) {
                            dialogRef.componentInstance.titleGermanMissing = true;
                        }
                        if (response.titleSerbianMissing) {
                            dialogRef.componentInstance.titleSerbianMissing = true;
                        }
                        if (response.descriptionMandatoryMissing) {
                            dialogRef.componentInstance.descriptionMandatoryMissing = true;
                        }
                        if (response.descriptionEnglishMissing) {
                            dialogRef.componentInstance.descriptionEnglishMissing = true;
                        }
                        if (response.descriptionGermanMissing) {
                            dialogRef.componentInstance.descriptionGermanMissing = true;
                        }
                        if (response.descriptionSerbianMissing) {
                            dialogRef.componentInstance.descriptionSerbianMissing = true;
                        }
                    }
                }
            }));
        });
    }
}
